import { IntegrationEnhanced, useOrgIntegrations } from "@/api/integrations";
import { EntitySelector } from "@/components/EntitySelector";
import { EntitySelectionProvider } from "@/components/EntitySelector/dnd/useEntitySelection";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { hasPlatformEntitySelector } from "@/constants/integration-platforms";
import type { IntegrationConnection } from "@cohesive/api-client";
import { type Entity, IntegrationIcon } from "@cohesive/ui-components";
import { AtSign } from "lucide-react";
import { useState } from "react";
import { useCanvasToolbarLayout } from "../../../context/CanvasToolbarLayoutContext";
import { CanvasToolbarDropdown } from "../CanvasToolbarDropdown";
import { CANVAS_TOOLBAR_MENU_MIN_WIDTH } from "../canvasToolbarLayout.constants";
import {
  CanvasInsertMenuExpandedHeader,
  INSERT_MENU_INTEGRATION_HEIGHT,
  INSERT_MENU_INTEGRATION_WIDTH,
} from "./CanvasInsertMenu";

type MenuView =
  | { kind: "menu" }
  | { kind: "integration"; integration: IntegrationEnhanced };

interface CanvasConnectorsMenuProps {
  /** Called when the menu opens — close bespoke Type-1 flyouts. */
  onOpen?: () => void;
  onEntitySelect: (entity: Entity) => void;
}

function buildConnectionForSelector(
  integration: IntegrationEnhanced,
): IntegrationConnection | null {
  if (!integration.integration_connection_id) {
    return null;
  }
  return {
    id: integration.integration_connection_id,
    integration_id: integration.id,
    integration_name: integration.name,
    integration_platform: integration.platform,
    integration_image_url: integration.metadata?.image_url,
    integration_provider: integration.provider,
    integration_provider_id: integration.provider_id,
    organization_id: "",
    user_id: "",
    status: "active",
    beta: false,
    config: integration.integration_connection_config || {},
    instructions: "",
    created_at: integration.created_at,
    updated_at: integration.updated_at,
  };
}

/**
 * Connectors — the external-integrations entry on the primary bar (design
 * system ruling: promoted out of the Insert menu to its own top-level
 * trigger; Insert keeps its copy). Same entries, same entity-selector flow.
 */
export function CanvasConnectorsMenu({
  onOpen,
  onEntitySelect,
}: CanvasConnectorsMenuProps) {
  const { tooltipSide, menuSide, submenuGapPx } = useCanvasToolbarLayout();
  const { data: integrations = [] } = useOrgIntegrations();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MenuView>({ kind: "menu" });

  const connectedIntegrations = integrations
    .filter(
      (i) =>
        i.connected &&
        i.integration_connection_id &&
        hasPlatformEntitySelector(i.platform),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  if (connectedIntegrations.length === 0) {
    return null;
  }

  const closeMenu = () => {
    setOpen(false);
    setView({ kind: "menu" });
  };

  const integrationView =
    view.kind === "integration"
      ? (() => {
          const connection = buildConnectionForSelector(view.integration);
          if (!connection) return null;
          return (
            <div
              className="flex flex-col overflow-hidden rounded-lg border border-n-10 bg-n-00 shadow-elev-5"
              style={{
                width: INSERT_MENU_INTEGRATION_WIDTH,
                height: INSERT_MENU_INTEGRATION_HEIGHT,
              }}
            >
              <CanvasInsertMenuExpandedHeader
                icon={
                  <IntegrationIcon
                    platform={view.integration.platform}
                    name={view.integration.name}
                    imageURL={view.integration.metadata?.image_url}
                    className="size-4 rounded"
                  />
                }
                label={view.integration.name}
                onBack={() => setView({ kind: "menu" })}
              />
              <div className="min-h-0 flex-1">
                <EntitySelectionProvider key={connection.id}>
                  <EntitySelector
                    key={connection.id}
                    connection={connection}
                    onClose={closeMenu}
                    onSelect={(entity) => {
                      onEntitySelect(entity);
                      closeMenu();
                    }}
                    className="flex-1 border-0"
                  />
                </EntitySelectionProvider>
              </div>
            </div>
          );
        })()
      : null;

  return (
    <CanvasToolbarDropdown
      label="Connectors"
      tooltipSide={tooltipSide}
      icon={<AtSign className="size-4" strokeWidth={2} />}
      showCaret={false}
      side={menuSide}
      align="center"
      sideOffset={submenuGapPx}
      minWidth={
        view.kind === "integration"
          ? INSERT_MENU_INTEGRATION_WIDTH
          : CANVAS_TOOLBAR_MENU_MIN_WIDTH
      }
      contentClassName={
        view.kind === "integration"
          ? "border-0 bg-transparent p-0 shadow-none"
          : undefined
      }
      onOpen={onOpen}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setView({ kind: "menu" });
        }
      }}
    >
      {view.kind === "integration" ? (
        integrationView
      ) : (
        <>
          {connectedIntegrations.map((integration) => (
            <DropdownMenuItem
              key={integration.id}
              onSelect={(event) => {
                event.preventDefault();
                setView({ kind: "integration", integration });
              }}
            >
              <IntegrationIcon
                platform={integration.platform}
                name={integration.name}
                imageURL={integration.metadata?.image_url}
                className="size-4 rounded"
              />
              {integration.name}
            </DropdownMenuItem>
          ))}
        </>
      )}
    </CanvasToolbarDropdown>
  );
}
